import {
  DashboardState,
  EActionCategory,
  EActionSubCategory,
  EDataBaseType,
  EDatasourceType,
  EUserAccessPart,
  type IActionDefinitionDto,
  type IProjectAccess
} from "@kuika/kuika-cl-model";
import LocalStorageService from "../../auth/local-storage-service";
import type { ScreenDesignInstance } from "./designer-context";
import { KMainFunctions } from "./main-functions";

/**
 * Returns true if the actionType represents a user-created datasource action (SQL, REST, C#, AI, etc.)
 * as opposed to a system/internal action type. User-created actions often have actionCategory=Other (0)
 * and should not be excluded by category or permission filters.
 */
function isUserDatasourceActionType(actionType: number): boolean {
  switch (actionType) {
    case EDatasourceType.ManagedDb:
    case EDatasourceType.CustomDb:
    case EDatasourceType.RestApi:
    case EDatasourceType.RestApiWOSwagger:
    case EDatasourceType.CSharpFunctions:
    case EDatasourceType.MsSql:
    case EDatasourceType.Oracle:
    case EDatasourceType.ManagedDbOracle:
    case EDatasourceType.ManagedDbPostgre:
    case EDatasourceType.Postgre:
    case EDatasourceType.AIActions:
    case EDatasourceType.AIAgent:
    case EDatasourceType.VisionAction:
    case EDatasourceType.SAP:
    case EDatasourceType.OData:
    case EDatasourceType.Mqtt:
    case EDatasourceType.JavaScriptAction:
      return true;
    default:
      return false;
  }
}

/**
 * Action types that can supply a DMS document parameter value (KOA-17254).
 *
 * <b>This list mirrors the code generator's own constraint exactly.</b>
 * `ActionDefinitionModel.IsDbAction` accepts these six and nothing else, and
 * `DocumentManagementControllerGenerator.ResolveSupplyingAction` throws for anything outside it — so showing a
 * wider set here would let a user pick an action that fails at generation time instead of at pick time.
 *
 * Note the deliberate absence of `EDatasourceType.MsSql` (8): that member exists only in the frontend enum —
 * the backend `EDatasourceTypes` has no value 8 at all — so an action of that type could never generate.
 */
const DOCUMENT_PARAMETER_SOURCE_ACTION_TYPES: ReadonlySet<number> = new Set<number>([
  EDatasourceType.ManagedDb,
  EDatasourceType.CustomDb,
  EDatasourceType.Oracle,
  EDatasourceType.ManagedDbOracle,
  EDatasourceType.ManagedDbPostgre,
  EDatasourceType.Postgre
]);

/**
 * Whether an action can supply a document parameter: it must run as a database query, and it must return a row
 * with fields — a parameter points at one of those fields, so an action that returns nothing can never fill one.
 * That second half also removes write statements (insert/update/delete) without having to guess at names.
 */
export function isDocumentParameterSourceAction(x: IActionDefinitionDto): boolean {
  if (!DOCUMENT_PARAMETER_SOURCE_ACTION_TYPES.has(x.actionType)) return false;
  return (x.returnTypeModel?.elements?.length ?? 0) > 0;
}

/**
 * Whether a datasource is one whose actions could supply a document parameter.
 *
 * The same six types, asked of the datasource rather than of a finished action — the question the designer has to
 * answer *before* an action exists, when the user asks to create one. `isDocumentParameterSourceAction` cannot
 * answer it: a brand new action has no return type yet, so it would reject every action the tool exists to author.
 *
 * Exported from here so the set stays written down once. It was duplicated in the tool's own action editor, which
 * meant the picker's idea of a usable source and the editor's idea of an editable one could drift apart silently.
 */
export function canDatasourceSupplyDocumentParameter(datasourceType: number | undefined): boolean {
  return isSqlActionDatasourceType(datasourceType);
}

/**
 * Whether a datasource's actions are SQL queries — the same six types, asked without the document-parameter
 * framing. It is also exactly the set `data-diagram-canvas.tsx` will mount `<CreateAction>` for, so any surface
 * that offers "create an action here" must ask this first: offering a seventh type opens an editor that renders
 * nothing. `MsSql` is deliberately absent — a MS SQL connection is persisted as `CustomDb`.
 */
export function isSqlActionDatasourceType(datasourceType: number | undefined): boolean {
  if (datasourceType === undefined) return false;
  return DOCUMENT_PARAMETER_SOURCE_ACTION_TYPES.has(datasourceType);
}

export interface ActionFilterContext {
  dashboardState: number | null;
  projectAccess: IProjectAccess[] | undefined;
  isBleServiceDataSource: boolean;
  isUserKuikaDeveloper: boolean;
  isCurrentProcessScreenRole: boolean;
  /**
   * Whether the surface asking is authoring document parameters (KOA-17254).
   *
   * Separate from `dashboardState` because that tool is reachable two ways: as a dashboard state, and as a popup
   * over the design canvas opened from a Document component's properties. In the popup the dashboard is still
   * `design`, so keying the restriction on the dashboard state would offer navigation, UI and write actions that
   * cannot supply a parameter value — the generator rejects them, so the picker would be advertising failures.
   *
   * Optional so existing callers keep compiling; absent reads as "derive it from the dashboard state", which is
   * what every caller outside the popup means.
   */
  isDocumentParameterAuthoring?: boolean;
}

/** Whether this context is authoring document parameters, however it was reached. */
export function isDocumentParameterAuthoringContext(ctx: ActionFilterContext): boolean {
  return ctx.isDocumentParameterAuthoring ?? ctx.dashboardState === DashboardState.documentParameters;
}

/**
 * Build the filter context from the action-picker's props.
 */
export function buildActionFilterContextFromProps(sd: {
  dashboardState: number;
  project?: { projectAccess?: IProjectAccess[]; processStepScreenIds?: Array<{ screenId: string }> };
  sdId?: string;
  activeEditingDatasource?: { datasourceType?: number };
  context?: { userInfo?: { username?: string } };
}): ActionFilterContext {
  return {
    dashboardState: sd.dashboardState,
    projectAccess: sd.project?.projectAccess,
    isBleServiceDataSource:
      sd.dashboardState === DashboardState.dataDiagramScheme &&
      sd.activeEditingDatasource?.datasourceType === EDatasourceType.BleService,
    isUserKuikaDeveloper: KMainFunctions.isUserKuikaDeveloper(sd.context?.userInfo?.username ?? ""),
    isCurrentProcessScreenRole: !!sd.project?.processStepScreenIds?.some((p) => p.screenId === sd.sdId)
  };
}

/**
 * Build the filter context from a ScreenDesignContextProvider (MCP tool path).
 */
export function buildActionFilterContextFromSd(sd: ScreenDesignInstance): ActionFilterContext {
  const curDashboard = sd.designSituationProvider.state.dashboardState;
  const project = sd.projectProvider.state.project;
  const activeDatasource = sd.datasourceProvider.state.activeEditingDatasource;
  const sdId = sd.screenProvider.state.sdId;

  return {
    dashboardState: curDashboard,
    projectAccess: project?.projectAccess,
    isBleServiceDataSource:
      curDashboard === DashboardState.dataDiagramScheme &&
      activeDatasource?.datasourceType === EDatasourceType.BleService,
    isUserKuikaDeveloper: false, // MCP tools don't have user context — be permissive
    isCurrentProcessScreenRole: !!project?.processStepScreenIds?.some((p) => p.screenId === sdId)
  };
}

/**
 * True when the action can run on the server — in an API method, a workflow step or a scheduled job.
 * `isActionDefinitionVisible` hides the rest from those backend surfaces.
 */
export function isServerRunnableActionDefinition(x: IActionDefinitionDto): boolean {
  return !(
    x.actionType === EDatasourceType.SystemJSFunctions ||
    x.actionType === EDatasourceType.CustomAuthorizationProvider ||
    x.actionType === EDatasourceType.CustomAuthenticationProvider ||
    x.actionType === EDatasourceType.CustomVerificationCodeSenderProvider ||
    x.actionType === EDatasourceType.CustomConnectionStringProvider ||
    x.actionType === EDatasourceType.CustomExternalTokenValidatorProvider ||
    x.actionType === EDatasourceType.CustomMailProvider ||
    x.actionCategory === EActionCategory.Navigation ||
    x.name === "DownloadReport"
  );
}

/**
 * Returns true if the action definition should be visible given the current context.
 * This is the single source of truth — used by both the action-picker UI and MCP tools.
 */
export function isActionDefinitionVisible(x: IActionDefinitionDto, ctx: ActionFilterContext): boolean {
  // Exclude "Other" category — but allow user-created datasource actions which default to Other (0)
  if (x.actionCategory === EActionCategory.Other && !isUserDatasourceActionType(x.actionType)) return false;

  // Exclude EAV sub-categories that are internal
  if (x.actionCategory === EActionCategory.EAV && x.actionSubCategory === EActionSubCategory.EnumItem) return false;
  if (x.actionCategory === EActionCategory.EAV && x.actionSubCategory === EActionSubCategory.Localization) return false;

  // Exclude internal template
  if (x.actionTemplate === "setDynamicAssignmentValue($scope)") return false;

  // Exclude deprecated
  if (x.isDeprecated === true) return false;

  // BLE context — only SystemJSFunctions
  if (ctx.isBleServiceDataSource && x.actionType !== EDatasourceType.SystemJSFunctions) return false;

  // Backend contexts — exclude UI-only actions
  if (
    (ctx.dashboardState === DashboardState.apiManagement ||
      ctx.dashboardState === DashboardState.workFlow ||
      ctx.dashboardState === DashboardState.scheduledJobs) &&
    !isServerRunnableActionDefinition(x)
  ) {
    return false;
  }

  // Document Parameters context — only actions that can actually supply a value (KOA-17254). UI actions,
  // navigation, providers and write statements are all irrelevant here, and the generator rejects them anyway.
  if (isDocumentParameterAuthoringContext(ctx)) {
    return isDocumentParameterSourceAction(x);
  }

  // Report context
  if (ctx.dashboardState === DashboardState.reportDesigner) {
    if (
      x.actionType === EDatasourceType.SystemCSFunctions ||
      x.actionType === EDatasourceType.SystemJSFunctions ||
      x.actionType === EDatasourceType.CustomAuthorizationProvider ||
      x.actionType === EDatasourceType.CustomAuthenticationProvider ||
      x.actionType === EDatasourceType.CustomVerificationCodeSenderProvider ||
      x.actionType === EDatasourceType.CustomConnectionStringProvider ||
      x.actionType === EDatasourceType.CustomExternalTokenValidatorProvider ||
      x.actionType === EDatasourceType.CustomMailProvider
    ) {
      return false;
    }
  }

  // Invers category only for Kuika developers
  if (!ctx.isUserKuikaDeveloper && x.actionCategory === EActionCategory.Invers) return false;

  // ResumeProcess only on process screens (not in scheduled jobs)
  if (
    (!ctx.isCurrentProcessScreenRole || ctx.dashboardState === DashboardState.scheduledJobs) &&
    x.name === "ResumeProcess"
  ) {
    return false;
  }

  // Permission: system JS functions require edit-system access
  const access = ctx.projectAccess ?? [];
  const hasAccessEditSystem = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
    EUserAccessPart.AddEditSystemActions,
    access
  );
  const hasAccessCustom = KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
    EUserAccessPart.ManageCustomActions,
    access
  );

  if (!hasAccessEditSystem && x.actionType === EDatasourceType.SystemJSFunctions) return false;
  if (
    hasAccessEditSystem &&
    !hasAccessCustom &&
    x.actionType !== EDatasourceType.SystemJSFunctions &&
    !isUserDatasourceActionType(x.actionType)
  )
    return false;

  // Oracle — no ImportMappedExcel
  if (LocalStorageService.getCurrentDomainManagedDbType() === EDataBaseType.Oracle && x.name === "ImportMappedExcel") {
    return false;
  }

  // Domain-specific
  if (x.name === "PrintTcpl" && LocalStorageService.getCurrentDomain() !== "YildizPul") return false;

  return true;
}

/**
 * Filter an array of action definitions using the shared visibility rules.
 * Convenience wrapper for MCP tool usage.
 */
export function filterActionDefinitions(
  definitions: IActionDefinitionDto[],
  sd: ScreenDesignInstance
): IActionDefinitionDto[] {
  const ctx = buildActionFilterContextFromSd(sd);
  return definitions.filter((x) => isActionDefinitionVisible(x, ctx));
}
